import { AssemblyLine, Guardrail, GuardrailEnforcement } from '../types';

/* ------------------------------------------------------------------ */
/*  AssemblyLine import / export                                            */
/*                                                                    */
/*  A "draft" is the portable, re-importable shape of a assemblyLine: the   */
/*  same body POST /assembly-lines accepts, minus server-owned fields (id,   */
/*  slug, version, timestamps). Export produces one; import parses    */
/*  one and hands it to the editor as an unsaved new assemblyLine.          */
/* ------------------------------------------------------------------ */

export interface AssemblyLineDraftStep {
  key: string;
  label: string;
  desc: string;
  skillRef: string;
  order?: number;
  dependsOn?: string[];
  guardrails?: Guardrail[];
  confluenceStepSync?: boolean;
  gitStepSync?: boolean;
  required?: boolean;
  inputs?: unknown[];
  outputs?: unknown[];
  runLabel?: string;
  rerunLabel?: string;
}

export interface AssemblyLineDraftPhase {
  key: string;
  label: string;
  desc: string;
  order?: number;
  required?: boolean;
  steps: AssemblyLineDraftStep[];
}

export interface AssemblyLineDraft {
  name: string;
  description?: string;
  brandLabel?: string;
  brandHeadline?: string;
  brandSubheadline?: string;
  metadataTabKey?: string;
  platformOptions?: string[];
  phases: AssemblyLineDraftPhase[];
}

/** Server-owned fields that must never round-trip through export/import. */
type ExportableAssemblyLine = Omit<AssemblyLine, 'id' | 'slug' | 'version' | 'createdAt' | 'updatedAt'>;

/** Strip a fully-loaded assemblyLine down to its portable, re-importable shape. */
export function toAssemblyLineExport(b: AssemblyLine): ExportableAssemblyLine {
  return {
    name: b.name,
    description: b.description,
    brandLabel: b.brandLabel,
    brandHeadline: b.brandHeadline,
    brandSubheadline: b.brandSubheadline,
    metadataTabKey: b.metadataTabKey,
    platformOptions: b.platformOptions ?? [],
    phases: b.phases.map((p) => ({
      key: p.key,
      label: p.label,
      desc: p.desc,
      order: p.order,
      steps: p.steps.map((s) => ({
        key: s.key,
        label: s.label,
        desc: s.desc,
        skillRef: s.skillRef,
        order: s.order,
        dependsOn: s.dependsOn,
        guardrails: s.guardrails,
        confluenceStepSync: s.confluenceStepSync,
        gitStepSync: s.gitStepSync,
        inputs: s.inputs,
        outputs: s.outputs,
        runLabel: s.runLabel,
        rerunLabel: s.rerunLabel,
      })),
    })),
  };
}

/** Filesystem-safe base name for a downloaded assemblyLine. */
export function assemblyLineFileName(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'assemblyLine'}.assemblyLine.json`;
}

/** Trigger a browser download of `data` serialized as pretty JSON. */
export function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * Defensively parse a step's `guardrails` array from imported JSON. Entries
 * without text are dropped; the server re-validates on save, so this only needs
 * to be safe, not exhaustive. A missing id is backfilled so the entry survives.
 */
function parseGuardrails(v: unknown): Guardrail[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out: Guardrail[] = [];
  for (const raw of v) {
    if (!raw || typeof raw !== 'object') continue;
    const g = raw as Record<string, unknown>;
    const text = str(g.text).trim();
    if (!text) continue;
    const enforcement: GuardrailEnforcement =
      g.enforcement === 'warn' ? 'warn' : g.enforcement === 'off' ? 'off' : 'block';
    out.push({
      id: str(g.id) || `g-${Math.random().toString(36).slice(2, 8)}`,
      text,
      enforcement,
    });
  }
  return out;
}

/**
 * Parse a assemblyLine JSON file into a {@link AssemblyLineDraft}, throwing a
 * human-readable Error on anything malformed. Accepts either a bare draft
 * object or one wrapped as `{ "assemblyLine": { ... } }` (the shape some tools
 * emit). Server-side validation (skill refs, dependency cycles) still runs
 * on save — this is just enough to fill the editor safely.
 */
export function parseAssemblyLineJson(text: string): AssemblyLineDraft {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new Error('File is not valid JSON.');
  }
  if (!raw || typeof raw !== 'object') {
    throw new Error('Expected a JSON object describing an assembly line.');
  }

  // Unwrap a `{ assemblyLine: {...} }` envelope if present.
  const obj = raw as Record<string, unknown>;
  const body = (obj.assemblyLine && typeof obj.assemblyLine === 'object' ? obj.assemblyLine : obj) as Record<string, unknown>;

  const name = str(body.name).trim();
  if (!name) throw new Error('Assembly Line "name" is required.');

  if (body.phases != null && !Array.isArray(body.phases)) {
    throw new Error('"phases" must be an array.');
  }

  const rawPhases = (Array.isArray(body.phases) ? body.phases : []) as Record<string, unknown>[];
  const phases: AssemblyLineDraftPhase[] = rawPhases.map((p, pi) => {
    if (!p || typeof p !== 'object') throw new Error(`Phase #${pi + 1} is not an object.`);
    if (p.steps != null && !Array.isArray(p.steps)) {
      throw new Error(`Phase "${str(p.key) || pi + 1}" has a non-array "steps".`);
    }
    const rawSteps = (Array.isArray(p.steps) ? p.steps : []) as Record<string, unknown>[];
    const steps: AssemblyLineDraftStep[] = rawSteps.map((s, si) => {
      if (!s || typeof s !== 'object') throw new Error(`Step #${si + 1} in phase "${str(p.key) || pi + 1}" is not an object.`);
      const dependsOn = Array.isArray(s.dependsOn) ? (s.dependsOn as unknown[]).map(str).filter(Boolean) : undefined;
      return {
        key: str(s.key),
        label: str(s.label),
        desc: str(s.desc),
        skillRef: str(s.skillRef),
        order: typeof s.order === 'number' ? s.order : si,
        dependsOn,
        guardrails: parseGuardrails(s.guardrails),
        confluenceStepSync: typeof s.confluenceStepSync === 'boolean' ? s.confluenceStepSync : undefined,
        gitStepSync: typeof s.gitStepSync === 'boolean' ? s.gitStepSync : undefined,
        required: typeof s.required === 'boolean' ? s.required : undefined,
        inputs: Array.isArray(s.inputs) ? (s.inputs as unknown[]) : undefined,
        outputs: Array.isArray(s.outputs) ? (s.outputs as unknown[]) : undefined,
        runLabel: typeof s.runLabel === 'string' ? s.runLabel : undefined,
        rerunLabel: typeof s.rerunLabel === 'string' ? s.rerunLabel : undefined,
      };
    });
    return {
      key: str(p.key),
      label: str(p.label),
      desc: str(p.desc),
      order: typeof p.order === 'number' ? p.order : pi,
      required: typeof p.required === 'boolean' ? p.required : undefined,
      steps,
    };
  });

  // Preserve any explicit ordering authored in the file.
  phases.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  for (const ph of phases) ph.steps.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return {
    name,
    description: str(body.description),
    brandLabel: typeof body.brandLabel === 'string' ? body.brandLabel : undefined,
    brandHeadline: typeof body.brandHeadline === 'string' ? body.brandHeadline : undefined,
    brandSubheadline: typeof body.brandSubheadline === 'string' ? body.brandSubheadline : undefined,
    metadataTabKey: typeof body.metadataTabKey === 'string' ? body.metadataTabKey : undefined,
    platformOptions: Array.isArray(body.platformOptions)
      ? (body.platformOptions as unknown[]).map(str).filter(Boolean)
      : undefined,
    phases,
  };
}
